import React from 'react';
import { 
  LuChevronRight, 
  LuFileText, 
  LuInfo 
} from 'react-icons/lu';

// Safe, version-independent status indicators
import { 
  FiCheckCircle, 
  FiClock, 
  FiAlertTriangle, 
  FiHelpCircle 
} from 'react-icons/fi';


export default function IncidentAuditFooterDashboard() {
  // Data for Recent Closed Incidents Table
  const closedIncidents = [
    { id: 'INC-021', incident: 'Spillage in cold room', location: 'Store Room', closedOn: '17 Jul 2026' },
    { id: 'INC-020', incident: 'Damaged light in kitchen', location: 'Kitchen', closedOn: '16 Jul 2026' },
    { id: 'INC-018', incident: 'Customer complaint – waiting time', location: 'Dining Area', closedOn: '15 Jul 2026' },
    { id: 'INC-017', incident: 'Broken glass in bar area', location: 'Bar Area', closedOn: '14 Jul 2026' },
    { id: 'INC-015', incident: 'Handwash soap dispenser empty', location: 'Kitchen', closedOn: '14 Jul 2026' },
  ];

  // Data for Corrective Actions Overview Table
  const correctiveActions = [
    { status: 'Completed', total: 15, overdue: 0, icon: FiCheckCircle, iconColor: 'text-emerald-600' }, // <--- Fixed LuCheckCircle2
    { status: 'In Progress', total: 5, overdue: 2, icon: FiClock, iconColor: 'text-orange-500' },          // <--- Fixed LuClock
    { status: 'Overdue', total: 2, overdue: 2, icon: FiAlertTriangle, iconColor: 'text-rose-600' },      // <--- Fixed LuAlertTriangle
    { status: 'Not Started', total: 0, overdue: 0, icon: FiHelpCircle, iconColor: 'text-slate-400' },      // <--- Fixed LuHelpCircle
  ];

  // Data for Key Documents & Evidence List
  const documents = [
    { name: 'Incident Report Log', date: 'Updated 18 Jul 2026' },
    { name: 'Corrective Action Log', date: 'Updated 17 Jul 2026' },
    { name: 'Near Miss Register', date: 'Updated 16 Jul 2026' },
    { name: 'RIDDOR Report Register', date: 'Updated 14 Jul 2026' },
    { name: 'Insurance Claim Log', date: 'Updated 12 Jul 2026' },
  ];

  return (
    <div className="w-full bg-slate-50 p-6 font-sans flex flex-col gap-6">
      
      {/* 3-Column Card Grid Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 max-w-[1800px] mx-auto items-stretch w-full">
        
        {/* Card 1: Recent Closed Incidents */}
        <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm flex flex-col justify-between min-h-[380px]">
          <div>
            <h2 className="text-[14px] font-bold text-indigo-950 mb-4">
              Recent Closed Incidents
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-indigo-50/40 text-[9px] font-bold text-indigo-950/70 tracking-wider">
                    <th className="p-2.5 rounded-l-lg">ID</th>
                    <th className="p-2.5 w-[40%]">INCIDENT</th>
                    <th className="p-2.5">LOCATION</th>
                    <th className="p-2.5">CLOSED ON</th>
                    <th className="p-2.5 text-center rounded-r-lg">STATUS</th>
                  </tr>
                </thead>
                <tbody className="text-[11px] font-semibold text-slate-700 divide-y divide-slate-50">
                  {closedIncidents.map((row, index) => (
                    <tr key={index} className="hover:bg-slate-50/40 transition-colors">
                      <td className="p-2.5 font-bold text-slate-400 whitespace-nowrap">{row.id}</td>
                      <td className="p-2.5 font-bold text-slate-800">{row.incident}</td>
                      <td className="p-2.5 text-slate-600">{row.location}</td>
                      <td className="p-2.5 text-slate-900 font-medium whitespace-nowrap">{row.closedOn}</td>
                      <td className="p-2.5 text-center">
                        <span className="inline-block px-1.5 py-0.5 bg-emerald-50 text-emerald-600 text-[10px] font-extrabold border border-emerald-100 rounded-md tracking-wide">
                          Closed
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <button className="mt-4 flex items-center justify-start gap-1 w-fit text-[11px] font-bold text-blue-600 hover:text-blue-700 transition-colors py-1">
            <span>View all closed incidents</span>
            <LuChevronRight className="text-xs stroke-[2.5]" />
          </button>
        </div>

        {/* Card 2: Corrective Actions Overview */}
        <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm flex flex-col justify-between min-h-[380px]">
          <div>
            <h2 className="text-[14px] font-bold text-indigo-950 mb-4">
              Corrective Actions Overview
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-indigo-50/40 text-[9px] font-bold text-indigo-950/70 tracking-wider">
                    <th className="p-2.5 rounded-l-lg w-[45%]">STATUS</th>
                    <th className="p-2.5 text-center">TOTAL</th>
                    <th className="p-2.5 text-center rounded-r-lg">OVERDUE</th>
                  </tr>
                </thead>
                <tbody className="text-[11px] font-bold text-slate-700 divide-y divide-slate-50">
                  {correctiveActions.map((row, index) => {
                    const StatusIcon = row.icon;
                    return (
                      <tr key={index} className="hover:bg-slate-50/40 transition-colors">
                        <td className="p-2.5 flex items-center gap-2.5 text-slate-800">
                          <StatusIcon className={`${row.iconColor} text-sm flex-shrink-0`} />
                          <span>{row.status}</span>
                        </td>
                        <td className="p-2.5 text-center text-slate-900 font-extrabold">{row.total}</td>
                        <td className={`p-2.5 text-center ${row.overdue > 0 ? 'text-rose-600 font-extrabold' : 'text-slate-400 font-semibold'}`}>
                          {row.overdue}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          <button className="mt-4 flex items-center justify-start gap-1 w-fit text-[11px] font-bold text-blue-600 hover:text-blue-700 transition-colors py-1">
            <span>View all corrective actions</span>
            <LuChevronRight className="text-xs stroke-[2.5]" />
          </button>
        </div>

        {/* Card 3: Key Documents & Evidence */}
        <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm flex flex-col justify-between min-h-[380px]">
          <div>
            <h2 className="text-[14px] font-bold text-indigo-950 mb-5">
              Key Documents & Evidence
            </h2>
            <div className="flex flex-col gap-3.5">
              {documents.map((doc, index) => (
                <div key={index} className="flex items-center justify-between text-[11px] font-semibold">
                  <div className="flex items-center gap-2.5 text-indigo-950">
                    <LuFileText className="text-indigo-600 text-sm flex-shrink-0" />
                    <span>{doc.name}</span>
                  </div>
                  <span className="text-slate-400 font-normal ml-2 whitespace-nowrap">
                    {doc.date}
                  </span>
                </div>
              ))}
            </div>
          </div>
          <button className="mt-4 flex items-center justify-start gap-1 w-fit text-[11px] font-bold text-blue-600 hover:text-blue-700 transition-colors py-1">
            <span>View all documents</span>
            <LuChevronRight className="text-xs stroke-[2.5]" />
          </button>
        </div>

      </div>

      {/* Bottom Legal/Audit Alert Banner */}
      <div className="w-full max-w-[1800px] mx-auto bg-indigo-50/40 border border-indigo-100/50 rounded-xl px-4 py-3 flex items-center gap-2.5">
        <LuInfo className="text-blue-600 text-sm flex-shrink-0" />
        <p className="text-[10.5px] font-medium text-slate-500 tracking-wide">
          All incident records are time stamped and securely stored for compliance and audit purposes.
        </p>
      </div>

    </div>
  );
}
